import React, { useEffect, useState } from "react";
import "./Blocks.scss";
import { B1, B2, H2, H3, L, SLableIcon } from "../../ui/Typography";
import {
  Button,
  ButtonWithIcon,
  ButtonWithTextIcon,
  Checkbox,
  Dropdown,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectButton,
  Textbox,
} from "../../ui";
import {
  Block,
  DoubleArrowRight,
  Edit,
  Filter,
  Rating,
  Share,
  Trash,
  WarningIcon,
  Info,
} from "../../ui/icons";
import NotificationCard from "../../components/NotificationCard";
/* api call dependencies */
import { useDispatch, useSelector } from "react-redux";
import {
  addCamera,
  deleteCamera,
  fetchCameraList,
  testCameraConnection,
} from "../../redux/reducers/CameraReducer";
/* api call dependencies */

import Loading from "../../components/Loading";

/* images */
import block2 from "../../assets/images/live-image-2.png";
/* end images */

// Constants
const userId = "e919848e-43c8-4339-8a58-87716a288d66";

function Blocks() {
  const user = useSelector((state) => state.auth.user); // get user if logged in

  const dispatch = useDispatch(); // fro API calling
  const { cameras, loading, error } = useSelector((state) => state.camera);

  /* === useState === */

  const [formData, setFormData] = useState({
    UserID: userId,
    Camera_Name: "",
    Camera_Location: "",
    Camera_Stream: "", // default to Auto
    Camera_Mode: "true",

    // Manual connection fields
    Camera_Input_User: "",
    Camera_Input_Password: "",
    Camera_Input_Port: "",
    Camera_Input_URL: "",
  }); // state to gether form data for later calling add camera api

  const [errors, setErrors] = useState({}); // Form validation errors
  const [testResult, setTestResult] = useState(null); // API response after testing camera connection
  const [showTestLoading, setShowTestLoading] = useState(true); // Loading indicator for test connection
  const [showConnection, setShowConnection] = useState(false); // State to control display of add block/camera model
  const [showBlock, setShowBlock] = useState(false); // State to control display of block/camera confirmation modal
  const [showLoading, setShowLoading] = useState(true); // Global loading screen control (for initial and refresh fetch)
  const [isChecked, setIsChecked] = useState(false); // Checkbox state for select-all logic

  /* === End useState === */

  /* === useEffect === */

  /* useEffect: is-checked left section */
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.classList.contains("block-box-checkbox")) {
          const child = mutation.target;
          const parent = child.closest(".block-boxes");
          if (parent) {
            if (child.classList.contains("checked")) {
              parent.classList.add("has-checked");
            } else {
              parent.classList.remove("has-checked");
            }
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  /* useEffect: Initial fetch of camera list */
  useEffect(() => {
    const MIN_LOADING_TIME = 1000; // 1 second
    const start = Date.now();

    const fetchData = async () => {
      await dispatch(fetchCameraList(userId));
      const elapsed = Date.now() - start;
      const remainingTime = MIN_LOADING_TIME - elapsed;
      if (remainingTime > 0) {
        setTimeout(() => setShowLoading(false), remainingTime);
      } else {
        setShowLoading(false);
      }
    };

    fetchData();
  }, [dispatch, userId]);

  /* === End useEffect === */

  // === Handler: For native input changes ===
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // === Handler: For custom dropdowns for FormData ===
  const handleFormCustomChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // === Handler: Toggle select-all checkbox ===
  const handleSelectAllChange = (checked) => {
    setIsChecked(checked);
  };

  // === Form validation based on camera mode ===
  const validate = () => {
    const newErrors = {};

    if (!formData.Camera_Name.trim())
      newErrors.Camera_Name = "Camera name is required";
    if (!formData.Camera_Location)
      newErrors.Camera_Location = "Location is required";

    if (formData.Camera_Mode === "true") {
      if (!formData.Camera_Stream.trim())
        newErrors.Camera_Stream = "Camera ID is required";
    } else if (formData.Camera_Mode === "false") {
      if (!formData.Camera_Input_User.trim())
        newErrors.Camera_Input_User = "Username is required";
      if (!formData.Camera_Input_Password.trim())
        newErrors.Camera_Input_Password = "Password is required";
      if (!formData.Camera_Input_Port.trim())
        newErrors.Camera_Input_Port = "Port is required";
      if (!formData.Camera_Input_URL.trim())
        newErrors.Camera_Input_URL = "URL is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // === API Call: Test camera connection and show result in modal ===
  const handleTestConnection = async () => {
    if (!validate()) return; // validate before proceed
    const testCameraDta = {
      UserID: formData.UserID,
      Camera_Mode: formData.Camera_Mode,
      Camera_Stream: formData.Camera_Stream,
      Camera_Input_URL: formData.Camera_Input_URL,
    }; // collect data for send to test camera api
    setShowTestLoading(true);
    try {
      const resultAction = await dispatch(testCameraConnection(testCameraDta));

      // Handle success
      if (testCameraConnection.fulfilled.match(resultAction)) {
        const responseData = resultAction.payload;
        console.log(responseData);
        // Check if data is valid and not indicating 'not found'
        if (
          responseData &&
          !responseData.error &&
          responseData.status !== 404
        ) {
          setTestResult(responseData); // <- success response
          setShowBlock(true);
        } else {
          setTestResult({ error: "Camera not found" });
          setShowBlock(false); // Explicitly keep the modal closed
        }

        // console.log("Connection Test Successful:", resultAction.payload);
      }
      // Handle rejected case
      else {
        const errorMessage =
          resultAction.payload?.message ||
          resultAction.payload ||
          "Camera not found";
        setTestResult({ error: errorMessage });
        setShowBlock(false); // Prevent modal opening
        /* setTestResult({ error: resultAction.payload }); */ // <- error response
        // console.error("Connection Test Failed:", resultAction.payload);
      }
    } catch (error) {
      console.error("Unexpected Error:", error);
      setTestResult({ error: "Unexpected error occurred" });
    } finally {
      setShowTestLoading(false);
    }
  };

  // === API Call: Add new camera and refresh camera list ===
  const handleAddCamera = async () => {
    try {
      const resultAdding = await dispatch(addCamera(formData));
      if (addCamera.fulfilled.match(resultAdding)) {
        setShowLoading(true);
        // ✅ Refresh the camera list manually
        await dispatch(fetchCameraList(userId));
        setShowLoading(false);

        // ✅ Close modals
        setShowBlock(false);
        setShowConnection(false);
        /* console.log("Adding Block/Camera Successful:", resultAdding.payload); */
        /* // ✅ Refresh the page
        window.location.reload(); */
      } else {
        console.error("Adding Block/Camera Failed:", resultAdding.payload);
      }
    } catch (error) {
      console.error("Unexpected Adding Camera Error:", error);
    }
  };

  // === API Call: Delete selected camera ===
  const handelDeleteCamera = async (userid, cameraStream) => {
    try {
      const resultDelete = await dispatch(
        deleteCamera({ userId: userid, cameraStream })
      );
      if (deleteCamera.fulfilled.match(resultDelete)) {
        console.log("Delete Block/Camera Successful:", resultDelete.payload);
      } else {
        console.error("Delete Block/Camera Failed:", resultDelete.payload);
      }
    } catch (error) {
      console.error("Unexpected Deleting Camera Error:", error);
    }
  };

  // === Model: handel opening of the model ===
  const handleCloseConnModel = () => setShowConnection(false);
  const handleCloseBlockModel = () => setShowBlock(false);

  // === UI Conditional Rendering ===
  if (loading || showLoading) return <Loading title="Loading Cameras..." />;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="block-wrapper">
      <div className="block-container">
        <div className="block-left">
          <div className="borderWrapper w-100 h-100">
            <div className="borderInner give-flex-height">
              <div className="block-inner-box shadow-bottom">
                <div className="block-header">
                  <div className="block-header-title">
                    <H3>My Blocks</H3>
                  </div>
                  <div className="block-header-btn">
                    <ButtonWithIcon icon={Filter} size="md" />
                    <ButtonWithIcon icon={Trash} size="md" />
                    <ButtonWithIcon icon={Share} size="md" />
                    {user && (
                      <ButtonWithTextIcon
                        icon={Block}
                        onClick={() => setShowConnection(true)}
                      >
                        Add New Block
                      </ButtonWithTextIcon>
                    )}
                  </div>
                </div>
                {Array.isArray(cameras) && cameras.length > 0 ? (
                  <div className="borderWrapper w-100 select-all-wrapper">
                    <div className="borderInner">
                      <div className="select-all-box">
                        <Checkbox
                          name="selectAll"
                          className="select-all-checkbox"
                          size="lg"
                          id="selectAll"
                          label="Select all blocks"
                          checked={isChecked}
                          onChange={handleSelectAllChange}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {/* start of this section might be the multiple section */}
                {Array.isArray(cameras) && cameras.length > 0 ? (
                  cameras.map((camera, index) => (
                    <div
                      className={`borderWrapper w-100 block-boxes ${
                        camera.Camera_ONOFFstatus ? "online" : "offline"
                      }`}
                      key={index}
                    >
                      <div className="borderInner">
                        <div className="blocks-container-box">
                          <div className="block-box-img-box">
                            {/* <img
                              src={block1}
                              className="block-box-img"
                              alt="block image"
                            /> */}
                            <iframe
                              src={camera.Camera_URL}
                              title={`Camera-${index}`}
                              className="block-box-img"
                              width="100%"
                              height="300"
                              style={{
                                border: "none",
                                borderRadius: "0",
                                marginTop: "0",
                              }}
                              allowFullScreen
                            ></iframe>
                            <Checkbox
                              id="firstCheck"
                              className="block-box-checkbox"
                              name="checkbox2"
                              label=""
                              size="lg"
                              checked={isChecked}
                              /* onChange={(val) => console.log("Checked:", val)} */
                            />
                          </div>
                          <div className="block-box-details">
                            <div className="block-box-upper-details">
                              <div className="block-box-upper-left">
                                <span className="block-box-heading">
                                  <Block size="md" />
                                  {camera.Camera_Name}
                                </span>
                                <B2 className="block-box-location">
                                  PNTC veranda
                                </B2>
                                <Rating
                                  rating="3.5"
                                  size="md"
                                  className="ratting-icon"
                                />
                              </div>
                              <div className="block-box-upper-right">
                                <ButtonWithIcon icon={Edit} size="md" />
                                {user && (
                                  <ButtonWithIcon
                                    icon={Trash}
                                    size="md"
                                    onClick={() =>
                                      handelDeleteCamera(
                                        camera.UserID,
                                        camera.Camera_Stream
                                      )
                                    }
                                  />
                                )}
                                <ButtonWithIcon
                                  icon={DoubleArrowRight}
                                  to="view"
                                  size="md"
                                />
                              </div>
                            </div>
                            <div className="block-box-bellow-details">
                              <div className="block-box-bellow">
                                <span>Live</span>
                                <span>Enabled</span>
                                <span>Shared</span>
                              </div>
                              <B1 className="added-details">
                                Added on
                                <span className="bold"> 12/12/2024</span>
                              </B1>
                              <B1 className="added-details">
                                Added by
                                <span className="bold"> Joseph Jobs</span>
                              </B1>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data-found">
                    <WarningIcon size="lg" className="icon" />
                    <H2 className="title">No camera available.</H2>
                  </div>
                )}
                {/* <div className="borderWrapper w-100 block-boxes offline">
                  <div className="borderInner">
                    <div className="blocks-container-box">
                      <div className="block-box-img-box">
                        <img
                          src={block2}
                          className="block-box-img"
                          alt="block image"
                        />
                        <Checkbox
                          id="secondCheck"
                          className="block-box-checkbox"
                          name="checkbox2"
                          label=""
                          size="lg"
                          checked={isChecked}
                          onChange={(val) => console.log("Checked:", val)}
                        />
                      </div>
                      <div className="block-box-details">
                        <div className="block-box-upper-details">
                          <div className="block-box-upper-left">
                            <span className="block-box-heading">
                              <Block size="md" />
                              Headquaters
                            </span>
                            <B2 className="block-box-location">PNTC veranda</B2>
                            <Rating
                              rating="4"
                              size="md"
                              className="ratting-icon"
                            />
                          </div>
                          <div className="block-box-upper-right">
                            <ButtonWithIcon icon={Edit} size="md" />
                            <ButtonWithIcon icon={DoubleArrowRight} size="md" to='view' />
                          </div>
                        </div>
                        <div className="block-box-bellow-details">
                          <div className="block-box-bellow">
                            <span>Live</span>
                            <span>Enabled</span>
                            <span>Shared</span>
                          </div>
                          <B1 className="added-details">
                            Added on
                            <span className="bold"> 12/12/2024</span>
                          </B1>
                          <B1 className="added-details">
                            Added by
                            <span className="bold"> Joseph Jobs</span>
                          </B1>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
                {/*  <div className="borderWrapper w-100 block-boxes offline">
                  <div className="borderInner">
                    <div className="blocks-container-box">
                      <div className="block-box-img-box">
                        <img
                          src={block3}
                          className="block-box-img"
                          alt="block image"
                        />
                        <Checkbox
                          id="thirdCheck"
                          className="block-box-checkbox"
                          name="checkbox2"
                          label=""
                          size="lg"
                          checked={isChecked}
                          onChange={(val) => console.log("Checked:", val)}
                        />
                      </div>
                      <div className="block-box-details">
                        <div className="block-box-upper-details">
                          <div className="block-box-upper-left">
                            <span className="block-box-heading">
                              <Block size="md" />
                              Headquaters
                            </span>
                            <B2 className="block-box-location">PNTC veranda</B2>
                            <Rating
                              rating="3.5"
                              size="md"
                              className="ratting-icon"
                            />
                          </div>
                          <div className="block-box-upper-right">
                            <ButtonWithIcon icon={Edit} size="md" />
                            <ButtonWithIcon icon={DoubleArrowRight} size="md" />
                          </div>
                        </div>
                        <div className="block-box-bellow-details">
                          <div className="block-box-bellow">
                            <span>Live</span>
                            <span>Enabled</span>
                            <span>Shared</span>
                          </div>
                          <B1 className="added-details">
                            Added on
                            <span className="bold"> 12/12/2024</span>
                          </B1>
                          <B1 className="added-details">
                            Added by
                            <span className="bold"> Joseph Jobs</span>
                          </B1>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="borderWrapper w-100 block-boxes offline">
                  <div className="borderInner">
                    <div className="blocks-container-box">
                      <div className="block-box-img-box">
                        <img
                          src={block2}
                          className="block-box-img"
                          alt="block image"
                        />
                        <Checkbox
                          id="forthCheck"
                          className="block-box-checkbox"
                          name="checkbox2"
                          label=""
                          size="lg"
                          checked={isChecked}
                          onChange={(val) => console.log("Checked:", val)}
                        />
                      </div>
                      <div className="block-box-details">
                        <div className="block-box-upper-details">
                          <div className="block-box-upper-left">
                            <span className="block-box-heading">
                              <Block size="md" />
                              Headquaters
                            </span>
                            <B2 className="block-box-location">PNTC veranda</B2>
                            <Rating
                              rating="4"
                              size="md"
                              className="ratting-icon"
                            />
                          </div>
                          <div className="block-box-upper-right">
                            <ButtonWithIcon icon={Edit} size="md" />
                            <ButtonWithIcon icon={DoubleArrowRight} size="md" />
                          </div>
                        </div>
                        <div className="block-box-bellow-details">
                          <div className="block-box-bellow">
                            <span>Live</span>
                            <span>Enabled</span>
                            <span>Shared</span>
                          </div>
                          <B1 className="added-details">
                            Added on
                            <span className="bold"> 12/12/2024</span>
                          </B1>
                          <B1 className="added-details">
                            Added by
                            <span className="bold"> Joseph Jobs</span>
                          </B1>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
                {/* end of this section might be the multiple section */}
              </div>
            </div>
          </div>
        </div>
        <div className="block-right">
          <div className="borderWrapper w-100 h-100">
            <div className="borderInner give-flex-height">
              <div className="block-inner-box shadow-bottom">
                <div className="block-header">
                  <div className="block-header-title">
                    <H3>My Blocks</H3>
                  </div>
                </div>
                {/* start of this section might be the multiple section */}
                <NotificationCard
                  type="warning"
                  title="Mishandling of eye found"
                  id="CM-PNTC/4F/2212"
                  time="3 min ago"
                />
                <NotificationCard
                  type="danger"
                  title="Vijay Malia Detected in basement"
                  id="CM-PNTC/4F/2212"
                  time="2 min ago"
                />
                <NotificationCard
                  type="warning"
                  title="Mishandling of eye found"
                  id="CM-PNTC/4F/2212"
                  time="3 min ago"
                />
                <NotificationCard
                  type="danger"
                  title="Vijay Malia Detected in basement"
                  id="CM-PNTC/4F/2212"
                  time="2 min ago"
                />
                <NotificationCard
                  type="warning"
                  title="Mishandling of eye found"
                  id="CM-PNTC/4F/2212"
                  time="3 min ago"
                />{" "}
                <NotificationCard
                  type="warning"
                  title="Mishandling of eye found"
                  id="CM-PNTC/4F/2212"
                  time="3 min ago"
                />{" "}
                <NotificationCard
                  type="warning"
                  title="Mishandling of eye found"
                  id="CM-PNTC/4F/2212"
                  time="3 min ago"
                />
                {/* end of this section might be the multiple section */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showConnection} onHide={handleCloseConnModel}>
        <ModalHeader>My Blocks</ModalHeader>
        <ModalBody>
          <div className="body-content">
            {testResult?.error && (
              <SLableIcon size="xl" className="danger" icon={Info}>
                {testResult.error}
              </SLableIcon>
            )}
            <div className="form-group-box">
              <L forInput="location" size="md">
                Camera name
              </L>
              <Textbox
                size="lg"
                name="Camera_Name"
                value={formData.Camera_Name}
                onChange={handleFormChange}
                className="form-input"
              />
              {errors.Camera_Name && (
                <SLableIcon size="md" className="danger" icon={Info}>
                  {errors.Camera_Name}
                </SLableIcon>
              )}
            </div>
            <div className="form-group-box">
              <L forInput="location" size="md">
                Location
              </L>
              <Dropdown
                name="Camera_Location"
                className="form-input"
                size="lg"
                placeholder="Select you location"
                options={[{ label: "India", value: "IN" }]}
                value={formData.Camera_Location}
                onChange={(val) =>
                  handleFormCustomChange("Camera_Location", val)
                }
              />
              {errors.Camera_Location && (
                <SLableIcon size="md" className="danger" icon={Info}>
                  {errors.Camera_Location}
                </SLableIcon>
              )}
            </div>
            <div className="form-group-box">
              <L forInput="location" size="md">
                Connection Mode
              </L>
              <SelectButton
                options={[
                  { label: "Auto", value: "true" },
                  { label: "Manual", value: "false" },
                  { label: "Batch", value: "Batch" },
                ]}
                name="Camera_Mode"
                size="sm"
                value={formData.Camera_Mode}
                onChange={handleFormChange}
              />
            </div>
            {formData.Camera_Mode === "true" && (
              <div className="form-group-box">
                <L forInput="location" size="md">
                  Camera ID
                </L>
                <Textbox
                  size="lg"
                  name="Camera_Stream"
                  className="form-input"
                  value={formData.Camera_Stream}
                  onChange={handleFormChange}
                />
                {errors.Camera_Stream && (
                  <SLableIcon size="md" className="danger" icon={Info}>
                    {errors.Camera_Stream}
                  </SLableIcon>
                )}
              </div>
            )}
            {formData.Camera_Mode === "false" && (
              <>
                <div className="form-group-box grid-parent">
                  <div className="form-group-col grid-md-4">
                    <L forInput="location" size="md">
                      Username
                    </L>
                    <Textbox
                      size="lg"
                      name="Camera_Input_User"
                      className="form-input"
                      value={formData.Camera_Input_User}
                      onChange={handleFormChange}
                    />
                    {errors.Camera_Input_User && (
                      <SLableIcon size="md" className="danger" icon={Info}>
                        {errors.Camera_Input_User}
                      </SLableIcon>
                    )}
                  </div>
                  <div className="form-group-col grid-md-4">
                    <L forInput="location" size="md">
                      Password
                    </L>
                    <Textbox
                      type="password"
                      name="Camera_Input_Password"
                      size="lg"
                      className="form-input"
                      value={formData.Camera_Input_Password}
                      onChange={handleFormChange}
                    />
                    {errors.Camera_Input_Password && (
                      <SLableIcon size="md" className="danger" icon={Info}>
                        {errors.Camera_Input_Password}
                      </SLableIcon>
                    )}
                  </div>
                  <div className="form-group-col grid-md-4">
                    <L forInput="location" size="md">
                      Port
                    </L>
                    <Textbox
                      size="lg"
                      name="Camera_Input_Port"
                      className="form-input"
                      value={formData.Camera_Input_Port}
                      onChange={handleFormChange}
                    />
                    {errors.Camera_Input_Port && (
                      <SLableIcon size="md" className="danger" icon={Info}>
                        {errors.Camera_Input_Port}
                      </SLableIcon>
                    )}
                  </div>
                </div>
                <div className="form-group-box">
                  <L forInput="location" size="md">
                    URL
                  </L>
                  <Textbox
                    size="lg"
                    name="Camera_Input_URL"
                    className="form-input"
                    value={formData.Camera_Input_URL}
                    onChange={handleFormChange}
                  />
                  {errors.Camera_Input_URL && (
                    <SLableIcon size="md" className="danger" icon={Info}>
                      {errors.Camera_Input_URL}
                    </SLableIcon>
                  )}
                </div>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="model-button-box">
            <Button
              className="model-button"
              size="lg"
              onClick={handleTestConnection}
            >
              Test Connection
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal show={showBlock} onHide={handleCloseBlockModel} zIndex={1100}>
        <ModalHeader>My Blocks</ModalHeader>
        <ModalBody>
          <div className="body-content">
            <div className="grid-parent">
              <div className="grid-md-8">
                <div className="video-footage-box">
                  {/* <img
                    src={block1}
                    alt="video footage"
                    className="video-footage"
                  /> */}
                  {testResult?.TestCamera_URL && (
                    <iframe
                      src={testResult.TestCamera_URL}
                      title={`Test-Camera`}
                      className="video-footage"
                      width="100%"
                      height="300"
                      style={{
                        border: "none",
                        borderRadius: "0",
                        marginTop: "0",
                      }}
                      allowFullScreen
                    ></iframe>
                  )}
                  <p className="live-tag">Live</p>
                </div>
              </div>
              <div className="grid-md-4">
                <div className="block-details">
                  <div className="form-group-box">
                    <L forInput="location" size="md">
                      Block Name (camera)
                    </L>
                    <Textbox
                      size="lg"
                      className="form-input"
                      value={formData.Camera_Name}
                    />
                  </div>
                  <div className="form-group-box">
                    <L forInput="location" size="md">
                      Select Batch
                    </L>
                    <Dropdown
                      name="pixel-select"
                      size="lg"
                      placeholder="Select Batch"
                      options={[
                        { label: "Option 1", value: "opt1" },
                        { label: "Option 2", value: "opt2" },
                        { label: "Option 3", value: "opt3" },
                      ]}
                    />
                  </div>
                  <div className="form-group-box">
                    <L size="md" className="pre-get-data-label">
                      Location
                    </L>
                    <H3 className="pre-get-data">{formData.Camera_Location}</H3>
                  </div>
                  <div className="form-group-box">
                    <L className="pre-get-data-label" size="md">
                      Url
                    </L>
                    <H3 className="pre-get-data">
                      {testResult?.TestCamera_URL && testResult.TestCamera_URL}
                    </H3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="model-button-box">
            <Button
              className="model-button"
              size="lg"
              onClick={handleAddCamera}
            >
              Proceed Adding Block
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default Blocks;
